// Ultimate Card Game (UCG) - Bot AI hand scorer
//
// The context-aware utility score the bot gives every card in its hand: the
// card's own value from its stats, card-specific situational rules (spells,
// structures, the Salvage Company, ...), and the generic counter bonuses
// (air, armour, swarm, stealth, ...). Moved here unchanged from
// BotAI.evaluateHandUtility() (Phase 0.5, docs/AI_MASTERPLAN_2026-10.md);
// BotAI keeps the method as a thin facade because tests and the bot's own
// action loop call it by that name. tests/ai_replay_golden_test.mjs pins every
// candidate score it produces.

import { playBiasPoints } from '../../data/aiDoctrines.js';
import { omniscienceOf } from './perception.js';
import { CARD_RULES, SITUATIONAL_RULES, isPrecisionStrike } from './cardRules.js';
import { evaluateHandCombos } from './stateProjector.js';

// Utility scoring across all 5 hand slots. `excludedSlots` (optional)
// holds slots CardSystem already rejected this tick, so a card that can't
// be played right now doesn't keep winning the scoring.
export function evaluateHandUtility(bot, hand, threats, currentPlasma, excludedSlots = null) {

  const candidates = [];
  const nameCounts = {};
  const pendingMeltSlots = new Set(bot.cardSystem?.pendingMelts?.[bot.team]?.slots || []);
  let hasFusibleNonPulpCard = false;
  let hasAffordableTankInHand = false;
  let hasFragileBacklineInHand = false;

  for (let s = 0; s < hand.length; s++) {
    const c = hand[s];
    if (c && !c.hidden && !c.pending && !pendingMeltSlots.has(s)) {
      nameCounts[c.name] = (nameCounts[c.name] || 0) + 1;
      if (c.name !== 'Pulp' && bot.cardSystem?.getFusionInfo?.(c, bot.team)) {
        hasFusibleNonPulpCard = true;
      }
      const effCost = bot.cardSystem ? bot.cardSystem.getEffectiveCost(bot.team, c) : (c.cost || 5);
      const isTankCard = !c.isSpell && !c.isStructure && !c.isAir && !c.isKamikaze &&
        ((c.hp || 0) >= 125 || (c.deflectionRating || 0) >= 0.20 || (c.reflectDamageRatio || 0) > 0);
      const isFragileBackline = !c.isSpell && !c.isStructure && !c.isAir && !c.isKamikaze &&
        (c.hp || 0) <= 95 && ((c.damage || 0) >= 25 || (c.range || 0) >= 10 || (c.splashRadius || 0) > 0);
      if (isTankCard && currentPlasma >= effCost) hasAffordableTankInHand = true;
      if (isFragileBackline) hasFragileBacklineInHand = true;
    }
  }

  const pulpCount = nameCounts['Pulp'] || 0;
  const hasTankOnBoard = Boolean(threats?.ownUnits?.some(u =>
    !u.isStructure && !u.isAir && ((u.hp || 0) >= 110 || (u.deflectionRating || 0) >= 0.20 || (u.reflectDamageRatio || 0) > 0)
  ));

  // Shared by every card rule (js/engine/ai/cardRules.js); card, slot, cost,
  // score and enemyGroundedGround are set per card before a rule runs.
  const ctx = { bot, hand, threats, currentPlasma, nameCounts, pulpCount, hasFusibleNonPulpCard, card: null, slot: 0, cost: 0, score: 0, enemyGroundedGround: 0 };

  for (let slot = 0; slot < hand.length; slot++) {
    const card = hand[slot];
    if (!card || card.hidden || card.pending) continue;
    if (excludedSlots?.has(slot)) continue;
    // Never play a card that is actively mid-fusion (0.35s pendingMelts window),
    // otherwise the bot cancels its own Hyper/Symbiosis unit!
    if (pendingMeltSlots.has(slot)) continue;

    // Base modules (js/engine/modules.js): a module already built, still
    // descending, or blocked by a full stack can never be played, so it
    // must not occupy a candidate slot - otherwise the bot sits on a dead
    // card forever (the failure mode documented in AI_BEHAVIOR_ANALYSIS.md).
    // If permanently unbuildable (already built or stack full, not just descending),
    // recycle the dead slot so the bot can draw a fresh playable card.
    if (card.isModule || card.type === 'Module') {
      const ms = bot.cardSystem.battlefield?.moduleSystem;
      if (ms && !ms.canBuild(bot.team, card.moduleId)) {
        if (!ms.isDescending?.(bot.team) && omniscienceOf(bot).freeDeadModuleDiscard) {
          hand[slot] = null;
          bot.slotAges[slot] = 0;
        }
        continue;
      }
    }

    const cost = bot.cardSystem.getEffectiveCost(bot.team, card);
    const isAffordable = currentPlasma >= cost;

    // Base combat value derived from stats (incorporating real DPS, range, splash & deflection)
    let score;
    if (card.isKamikaze) {
      // One-time burst damage, not continuous DPS
      score = (card.cost || 5) * 1.4 + (card.damage || 0) * 0.08 + (card.hp || 0) * 0.04;
      const enemyGroundedGround = threats.enemyGroundCount - (threats.enemyParachutingCount || 0);
      if (enemyGroundedGround >= 2 || threats.enemyArmoredUnits >= 1) {
        score += 25 * bot.counterSensitivity;
      }
    } else {
      const effectiveDps = card.attackSpeed
        ? ((card.damage || 0) * 0.5 + ((card.damage || 0) / Math.max(0.4, card.attackSpeed)) * 0.5)
        : (card.damage || 0);
      const rangeBonus = (card.range && card.range >= 10) ? Math.min(8, (card.range - 8) * 0.25) : 0;
      const splashBonus = ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0) ? 4 : 0;
      const armorBonus = (card.deflectionRating || 0) > 0 ? (card.deflectionRating * 14) : 0;
      score = (card.cost || 5) * 1.4 + effectiveDps * 0.4 + (card.hp || 0) * 0.04 + rangeBonus + splashBonus + armorBonus;
    }

    // Card-specific situational rules (js/engine/ai/cardRules.js), in their fixed order.
    for (const rule of CARD_RULES) {
      if (!rule.matches(card)) continue;
      ctx.card = card; ctx.slot = slot; ctx.cost = cost; ctx.score = score;
      score = rule.score(ctx);
    }

    // A plan can keep a card in hand for now (js/engine/ai/strategist.js holds()).
    // Never hold an urgent counter when that counter is urgently needed (e.g. Hacker against enemy Hacker)!
    const isUrgentCounter = card.name === 'Hacker' && (threats.enemyHasHacker || (threats.ownDisabledByHackerCount || 0) > 0);
    if (!isUrgentCounter && bot.strategist && bot.strategist.holds(card, hand)) score = -999;

    // Early exit if card is strictly forbidden (e.g. invalid spell timing)
    if (score <= -500) {
      candidates.push({ slot, card, cost, score: -999, isAffordable: false });
      continue;
    }

    // 1. Anti-Air Counter Priority
    if (threats.enemyAirUnits > 0 && threats.ownAirTargeters < threats.enemyAirUnits) {
      if (card.targetsAir !== false && !card.isSpell) {
        score += 35 * bot.counterSensitivity;
      } else if (!card.isSpell && !(card.name === 'Hacker' && (threats.enemyHasHacker || (threats.ownDisabledByHackerCount || 0) > 0))) {
        score -= 40 * bot.counterSensitivity; // Don't build pure ground artillery against air fleets
      }
    } else if (threats.enemyAirUnits === 0 && threats.recentAir && card.targetsAir !== false && !card.isSpell) {
      // Proactive memory: opponent recently deployed air units, maintain anti-air vigilance
      score += 15 * bot.counterSensitivity;
    }

    // 2. Armor / Deflection Breaker (Energy weapons ignore deflection)
    if (threats.enemyArmoredUnits >= 1) {
      if (card.projectileType === 'Energy' || card.deflectionScaling) {
        score += 25 * bot.counterSensitivity;
      }
    }

    // 2b. Anti-Infantry Flame Priority (Devil Patrol bonus vs Marines)
    if (threats.enemyInfantryCount >= 1 && card.antiMarineBonus) {
      score += 20 * bot.counterSensitivity;
    }

    // 3. Swarm / Cluster Splash Counter
    const enemyGroundedGround = threats.enemyGroundCount - (threats.enemyParachutingCount || 0);
    if (enemyGroundedGround >= 3) {
      if (card.splashRadius > 0 || card.capsuleSplashRadius > 0) {
        score += 30 * bot.counterSensitivity;
      }
    }

    // 4. Base Defense & Anchor Push
    if (threats.isPressedBack || threats.enemyNearBaseCount >= 2) {
      if ((card.isStructure || card.deployAtBonusIndicator) && card.canAttack !== false && card.name !== 'Claiming Pillar') {
        score += 25 * bot.counterSensitivity;
      }
    }

    // 4b. Base modules are permanent and unattackable, so their value does
    // not depend on the current threat picture the way a field structure's
    // does - a flat premium instead, scaled by the HP they fuse into the
    // base, plus a bonus while under pressure since that HP lands where it
    // is needed most.
    if (card.isModule || card.type === 'Module') {
      score += 20 + (card.hp || 0) * 0.02;
      if (threats.isPressedBack) score += 15 * bot.counterSensitivity;
    }

    // 4c. Frontline Sentinel / Cannot Attack Buildings (Anti Shade)
    if (card.cannotAttackBuildings && card.name !== 'Hacker' && card.unitType !== 'Hacker') {
      if (threats.enemyGroundCount === 0) {
        // If the enemy has no ground units, Anti Shade can only idle at the frontline
        // and cannot damage structures or the enemy base. Penalize heavily.
        score -= 45;
      } else {
        // Enemy has active ground forces: Anti Shade is a premier frontline assassin.
        score += 15 * bot.counterSensitivity;
        if (threats.isPressedBack || threats.enemyNearBaseCount >= 1) {
          score += 25 * bot.counterSensitivity;
        }
      }
    }

    // 4d. Anti-Stealth Counters (Detectors & Area Splash)
    const activeOppProfile = bot.opponentProfile || bot.liveOpponentProfile;
    if (threats.enemyHasCloakedThreats || threats.recentCloak || activeOppProfile?.hasStealth) {
      const teamHasDetection = bot.battlefield?.hasTeamDetection ? bot.battlefield.hasTeamDetection(bot.team) : false;
      const isDetectorCard = card.isDetector || card.detectsCloak || card.unitType === 'DelvingPillar' || card.name === 'Delving Pillar' || card.name === 'A9 Grizzly' || card.moduleId === 'intel_uplink';
      if (!teamHasDetection) {
        if (isDetectorCard) {
          score += 45 * bot.counterSensitivity; // Priority: deploy detection to reveal stealth units
        } else if (card.splashRadius > 0 || card.capsuleSplashRadius > 0) {
          score += 25 * bot.counterSensitivity; // Splash damage hits cloaked units through Phase Shift
        }
      }
    }

    // 4e. Anti-Siege & Anti-Fortress Counters (Precision Strike & Air Bypass)
    ctx.enemyGroundedGround = enemyGroundedGround;
    for (const rule of SITUATIONAL_RULES) {
      if (!rule.matches(card)) continue;
      ctx.card = card; ctx.slot = slot; ctx.cost = cost; ctx.score = score;
      score = rule.score(ctx);
    }

    // 4f. Anti-Glass-Cannon Priority (Snipe Pulp / Extreme DPS before they wipe the army)
    if (threats.enemyLethalGlassCannons > 0) {
      if (isPrecisionStrike(card)) {
        if (score > 0) score += 55 * bot.counterSensitivity;
      } else if ((card.splashRadius || 0) > 0 || (card.capsuleSplashRadius || 0) > 0) {
        score += 30 * bot.counterSensitivity;
      } else if (card.targetsAir !== false && (card.speed || 0) >= 3.0) {
        score += 20 * bot.counterSensitivity;
      }
    }

    // 4g. Opening Frontline Cadence (first 10 seconds of match)
    if (bot.gameTime < 10 && (threats?.ownUnits?.length || 0) === 0 && !card.isKamikaze && !card.isSpell) {
      if (card.tier === 1 || (card.cost || 10) <= 12) {
        score += 35;
      } else if ((card.cost || 10) >= 25 || card.type === 'Structure') {
        score -= 40;
      }
    }

    // 4h. Tank-First / Frontline Sequencing: when no friendly tank is on the board and hand holds
    // both an affordable frontline tank and a fragile ranged/glass-cannon unit, deploy the tank first!
    if (!hasTankOnBoard && hasAffordableTankInHand && hasFragileBacklineInHand && !card.isSpell && !card.isStructure && !card.isAir && !card.isKamikaze) {
      const isTankCard = (card.hp || 0) >= 125 || (card.deflectionRating || 0) >= 0.20 || (card.reflectDamageRatio || 0) > 0;
      const isFragileBackline = (card.hp || 0) <= 95 && ((card.damage || 0) >= 25 || (card.range || 0) >= 10 || (card.splashRadius || 0) > 0);
      if (isTankCard) {
        score += 22 * bot.counterSensitivity;
      } else if (isFragileBackline) {
        score -= 12;
      }
    }

    if (card.isAir || card.elementType === 'Air' || card.isFlyer) {
      if (threats.hasEnemySiegeLine || (threats.enemyStructuresCount >= 1 && threats.enemyGroundCount >= 2)) {
        // Ground lane is blocked by Monitor Lock/Cannon: air units fly straight past to snipe artillery and base!
        score += 25 * bot.counterSensitivity;
      }
    }

    // 5. Drilling Triplet & Pulp Wildcard Retention (3 identical cards or 2 + Pulp)
    if (bot.profile.holdTriplets) {
      const isDirectPair = nameCounts[card.name] === 2 && Boolean(bot.cardSystem?.getFusionInfo?.(card, bot.team));
      const isPulpWildcardPartner = (pulpCount >= 1 && card.name !== 'Pulp' && Boolean(bot.cardSystem?.getFusionInfo?.(card, bot.team))) ||
        (card.name === 'Pulp' && pulpCount <= 2 && hasFusibleNonPulpCard);
      if (isDirectPair || isPulpWildcardPartner) {
        // Small hold bias so drawing a 3rd card is favored over immediately dumping
        // (a Hyper Hunt plan holds the pair harder).
        score -= 8 - (bot.strategist ? bot.strategist.pairHoldBias() : 0);
      }
    }

    // 5b. Doctrine preference (hidden play style, js/data/aiDoctrines.js)
    score += playBiasPoints(bot.doctrine, card);
    // 5c. The plan the bot is following (js/engine/ai/strategist.js), capped.
    if (bot.strategist) score += bot.strategist.biasFor(card);

    // 6. Anti-Starvation Slot Age Rotation
    score += (bot.slotAges[slot] || 0) * 0.6;

    candidates.push({
      slot,
      card,
      cost,
      score,
      isAffordable
    });
  }

  // 7. Multi-Card Combinatorics Lookahead (Säule A)
  const lookaheadWeight = typeof bot.profile?.comboLookahead === 'number'
    ? bot.profile.comboLookahead
    : (bot.profile?.comboLookahead === false ? 0 : 1.0);
  if (lookaheadWeight > 0) {
    const view = bot._lastDecisionView || null;
    const baseScores = candidates.map(c => c.score);
    const comboEval = evaluateHandCombos(bot, hand, baseScores, view, threats, currentPlasma);
    if (comboEval) {
      bot._lastComboEval = comboEval;
      for (const cand of candidates) {
        if (comboEval.slotScoreBonuses[cand.slot]) {
          cand.score += comboEval.slotScoreBonuses[cand.slot] * lookaheadWeight;
        }
        if (comboEval.holdSlots.has(cand.slot)) {
          const isCriticalDefense = threats.isPressedBack || (threats.enemyNearBaseCount || 0) > 0;
          if (!isCriticalDefense) {
            cand.score -= 60 * lookaheadWeight;
          }
        }
      }
    }
  } else {
    bot._lastComboEval = null;
  }

  candidates.sort((a, b) => b.score - a.score);

  const bestAffordable = candidates.find(c => c.isAffordable && c.score > 0) || null;
  const bestOverall = candidates.find(c => c.score > 0) || null;

  return { candidates, bestAffordable, bestOverall };
}
